/**
 * Network-Aware Lazy Loading
 * Advanced lazy loading that adapts to network conditions and user behavior
 * @version 1.0.0
 */

class NetworkAwareLazyLoader {
  constructor() {
    this.networkSpeed = this.detectNetworkSpeed();
    this.isSlowConnection = this.networkSpeed === 'slow';
    this.saveData = this.detectSaveData();
    this.userScrollSpeed = 'normal';
    this.lastScrollTime = Date.now();
    this.lastScrollY = window.scrollY;
    this.observers = new Map();
    this.loadedElements = new WeakSet();
    this.init();
  }

  init() {
    // Monitor network changes
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        this.networkSpeed = this.detectNetworkSpeed();
        this.isSlowConnection = this.networkSpeed === 'slow';
        this.saveData = this.detectSaveData();
        this.updateObserverConfigs();
        console.log(`📡 Network changed: ${this.networkSpeed}`);
      });
    }

    // Monitor scroll speed
    this.monitorScrollSpeed();

    // Setup observers for different element types
    this.setupImageObserver();
    this.setupIframeObserver();
    this.setupBackgroundObserver();
    this.setupSectionObserver();

    // Process existing elements
    this.processElements();

    // Prefetch on hover (fast connections only)
    if (!this.isSlowConnection && !this.saveData) {
      this.setupHoverPrefetch();
    }
  }

  /**
   * Detect network speed
   */
  detectNetworkSpeed() {
    if (!('connection' in navigator)) return 'fast';

    const conn = navigator.connection;
    const effectiveType = conn.effectiveType;

    const speedMap = {
      'slow-2g': 'slow',
      '2g': 'slow',
      '3g': 'medium',
      '4g': 'fast'
    };

    return speedMap[effectiveType] || 'fast';
  }

  /**
   * Detect Save-Data mode
   */
  detectSaveData() {
    if ('connection' in navigator && 'saveData' in navigator.connection) {
      return navigator.connection.saveData;
    }
    return false;
  }

  /**
   * Monitor scroll speed to adjust loading behavior
   */
  monitorScrollSpeed() {
    let scrollTimeout;

    window.addEventListener('scroll', () => {
      const now = Date.now();
      const currentY = window.scrollY;
      const timeDiff = now - this.lastScrollTime;
      const distanceDiff = Math.abs(currentY - this.lastScrollY);

      // Calculate scroll speed (pixels per second)
      const speed = (distanceDiff / timeDiff) * 1000;

      if (speed > 1000) {
        this.userScrollSpeed = 'fast';
      } else if (speed > 500) {
        this.userScrollSpeed = 'normal';
      } else {
        this.userScrollSpeed = 'slow';
      }

      this.lastScrollTime = now;
      this.lastScrollY = currentY;

      // Update observers based on scroll speed
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateObserverConfigs();
      }, 150);
    }, { passive: true });
  }

  /**
   * Get adaptive root margin based on network and scroll speed
   */
  getAdaptiveRootMargin() {
    // Save-Data mode: minimal preloading
    if (this.saveData) return '0px';

    // Slow connection: minimal preloading
    if (this.isSlowConnection) return '50px';

    // Fast scrolling: aggressive preloading
    if (this.userScrollSpeed === 'fast') {
      return this.networkSpeed === 'fast' ? '800px' : '400px';
    }

    // Normal scrolling: standard preloading
    if (this.userScrollSpeed === 'normal') {
      return this.networkSpeed === 'fast' ? '500px' : '200px';
    }

    // Slow scrolling: conservative preloading
    return '200px';
  }

  /**
   * Setup image lazy loading observer
   */
  setupImageObserver() {
    const config = {
      root: null,
      rootMargin: this.getAdaptiveRootMargin(),
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, config);

    this.observers.set('images', observer);
  }

  /**
   * Setup iframe lazy loading observer (videos, embeds)
   */
  setupIframeObserver() {
    const config = {
      root: null,
      rootMargin: this.saveData ? '0px' : '300px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadIframe(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, config);

    this.observers.set('iframes', observer);
  }

  /**
   * Setup background image observer
   */
  setupBackgroundObserver() {
    const config = {
      root: null,
      rootMargin: this.getAdaptiveRootMargin(),
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadBackgroundImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, config);

    this.observers.set('backgrounds', observer);
  }

  /**
   * Setup section lazy loading observer
   */
  setupSectionObserver() {
    const config = {
      root: null,
      rootMargin: '400px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadSection(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, config);

    this.observers.set('sections', observer);
  }

  /**
   * Update observer configurations dynamically
   */
  updateObserverConfigs() {
    const newMargin = this.getAdaptiveRootMargin();

    // Recreate observers with new margins
    this.observers.get('images')?.disconnect();
    this.observers.get('backgrounds')?.disconnect();

    this.setupImageObserver();
    this.setupBackgroundObserver();

    // Re-observe elements
    this.processElements();
  }

  /**
   * Process all lazy-loadable elements on page
   */
  processElements() {
    // Images
    document.querySelectorAll('img[loading="lazy"], img[data-src]').forEach(img => {
      if (!this.loadedElements.has(img)) {
        this.observers.get('images')?.observe(img);
      }
    });

    // Iframes
    document.querySelectorAll('iframe[loading="lazy"], iframe[data-src]').forEach(iframe => {
      if (!this.loadedElements.has(iframe)) {
        this.observers.get('iframes')?.observe(iframe);
      }
    });

    // Background images
    document.querySelectorAll('[data-bg-src]').forEach(el => {
      if (!this.loadedElements.has(el)) {
        this.observers.get('backgrounds')?.observe(el);
      }
    });

    // Lazy sections
    document.querySelectorAll('[data-lazy-section]').forEach(section => {
      if (!this.loadedElements.has(section)) {
        this.observers.get('sections')?.observe(section);
      }
    });
  }

  /**
   * Load image
   */
  loadImage(img) {
    if (this.loadedElements.has(img)) return;

    const src = img.dataset.src || img.src;
    const srcset = img.dataset.srcset || img.srcset;

    if (srcset) img.srcset = srcset;
    if (src && img.src !== src) img.src = src;

    // Remove data attributes
    delete img.dataset.src;
    delete img.dataset.srcset;

    // Decode image for smoother rendering
    if ('decode' in img) {
      img.decode().catch(() => {
        // Ignore decode errors
      });
    }

    this.loadedElements.add(img);
    console.log('🖼️ Lazy loaded image:', src);
  }

  /**
   * Load iframe
   */
  loadIframe(iframe) {
    if (this.loadedElements.has(iframe)) return;

    const src = iframe.dataset.src;
    if (src) {
      iframe.src = src;
      delete iframe.dataset.src;
    }

    this.loadedElements.add(iframe);
    console.log('🎥 Lazy loaded iframe:', src);
  }

  /**
   * Load background image
   */
  loadBackgroundImage(el) {
    if (this.loadedElements.has(el)) return;

    const bgSrc = el.dataset.bgSrc;
    if (bgSrc) {
      el.style.backgroundImage = `url('${bgSrc}')`;
      delete el.dataset.bgSrc;
    }

    this.loadedElements.add(el);
    console.log('🎨 Lazy loaded background:', bgSrc);
  }

  /**
   * Load section (trigger section-specific scripts)
   */
  loadSection(section) {
    if (this.loadedElements.has(section)) return;

    const scriptSrc = section.dataset.lazySection;
    if (scriptSrc && window.resourceLoader) {
      window.resourceLoader.load(scriptSrc, 'medium', 'script').then(() => {
        section.classList.add('lazy-loaded');
        console.log('📦 Lazy loaded section:', scriptSrc);
      });
    }

    this.loadedElements.add(section);
  }

  /**
   * Setup hover prefetch for links (fast connections only)
   */
  setupHoverPrefetch() {
    let hoverTimeout;

    document.addEventListener('mouseover', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.href;

      // Only prefetch internal links
      if (!href.startsWith(window.location.origin)) return;

      // Prefetch after 100ms hover (prevents accidental hovers)
      hoverTimeout = setTimeout(() => {
        this.prefetchPage(href);
      }, 100);
    });

    document.addEventListener('mouseout', () => {
      clearTimeout(hoverTimeout);
    });
  }

  /**
   * Prefetch page
   */
  prefetchPage(url) {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
    console.log('⚡ Prefetching:', url);
  }

  /**
   * Get status
   */
  getStatus() {
    return {
      network: this.networkSpeed,
      saveData: this.saveData,
      scrollSpeed: this.userScrollSpeed,
      rootMargin: this.getAdaptiveRootMargin(),
      observedImages: document.querySelectorAll('img[loading="lazy"]').length,
      observedIframes: document.querySelectorAll('iframe[loading="lazy"]').length,
      observedBackgrounds: document.querySelectorAll('[data-bg-src]').length
    };
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.networkLazyLoader = new NetworkAwareLazyLoader();
    console.log('🚀 Network-Aware Lazy Loader initialized:', window.networkLazyLoader.getStatus());
  });
} else {
  window.networkLazyLoader = new NetworkAwareLazyLoader();
  console.log('🚀 Network-Aware Lazy Loader initialized:', window.networkLazyLoader.getStatus());
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NetworkAwareLazyLoader;
}
